// Shared field mapping definitions and utilities

// License field definitions grouped for better UI organization
export const LICENSE_FIELD_GROUPS = [
  {
    title: "License Information",
    fields: [
      { key: "license_type_name", label: "License Type", column: 0 },
      { key: "effective_date", label: "Effective Date", column: 1 },
      { key: "effective_end_date", label: "End Date", column: 0 },
      { key: "renewal_date", label: "Renewal Date", column: 1 },
      { key: "approved_until_date", label: "Approved Until Date", column: 0 },
      { key: "status", label: "Status", column: 1 },
      { key: "cancellation_reason", label: "Cancellation Reason", column: 0 },
      { key: "cancellation_date", label: "Cancellation Date", column: 1 },
    ],
  },
  {
    title: "Registrant Information",
    fields: [
      { key: "registrant_email", label: "Registrant Email", column: 0 },
      { key: "registrant_name", label: "Registrant Name", column: 1 },
      {
        key: "registrant_company_name",
        label: "Registrant Company Name",
        column: 0,
      },
      { key: "registrant_phone", label: "Registrant Phone", column: 1 },
      {
        key: "registrant_address1",
        label: "Registrant Address Line 1",
        column: 0,
      },
      {
        key: "registrant_address2",
        label: "Registrant Address Line 2",
        column: 1,
      },
      { key: "registrant_city", label: "Registrant City", column: 0 },
      { key: "registrant_state", label: "Registrant State", column: 1 },
      { key: "registrant_zipcode", label: "Registrant ZIP Code", column: 0 },
    ],
  },
  {
    title: "Property Information",
    fields: [
      { key: "property_apn", label: "Property APN", column: 0 },
      { key: "property_address1", label: "Property Address Line 1", column: 1 },
      { key: "property_address2", label: "Property Address Line 2", column: 0 },
      { key: "property_city", label: "Property City", column: 1 },
      { key: "property_state", label: "Property State", column: 0 },
      { key: "property_zipcode", label: "Property ZIP Code", column: 1 },
      { key: "property_latitude", label: "Property Latitude", column: 0 },
      { key: "property_longitude", label: "Property Longitude", column: 1 },
    ],
  },
];

/** Extra column groups for operator "upload registrations with payments" import. */
export const LICENSE_PAYMENT_FIELD_GROUPS = [
  {
    title: "Payment (each spreadsheet row)",
    fields: [
      {
        key: "payment_period_start_date",
        label: "Payment Period Start Date",
        column: 0,
      },
      {
        key: "payment_period_end_date",
        label: "Payment Period End Date",
        column: 1,
      },
      { key: "payment_date", label: "Payment Date", column: 0 },
      {
        key: "payment_registration_fee",
        label: "Registration Fee Paid",
        column: 1,
      },
      { key: "payment_late_fee", label: "Late Fee Paid", column: 1 },
      {
        key: "payment_processing_fee",
        label: "Payment Processing Fee",
        column: 0,
      },
      { key: "payment_method", label: "Payment Method", column: 1 },
    ],
  },
];

// Unregistered properties field definitions grouped for better UI organization
export const UNREGISTERED_FIELD_GROUPS = [
  {
    title: "Basic Information",
    fields: [
      { key: "license_type_name", label: "License Type Name", column: 0 },
      { key: "effective_date", label: "Effective Date", column: 1 },
      {
        key: "registrant_email",
        label: "Registrant Email",
        column: 0,
      },
    ],
  },
  {
    title: "Property Information (Optional)",
    fields: [
      { key: "property_address1", label: "Property Address 1", column: 0 },
      { key: "property_address2", label: "Property Address 2", column: 1 },
      { key: "property_city", label: "Property City", column: 0 },
      { key: "property_state", label: "Property State", column: 1 },
      { key: "property_zipcode", label: "Property ZIP Code", column: 0 },
      { key: "property_apn", label: "Property APN", column: 1 },
      { key: "property_latitude", label: "Property Latitude", column: 0 },
      { key: "property_longitude", label: "Property Longitude", column: 1 },
    ],
  },
  {
    title: "Owner Information (Optional)",
    fields: [
      { key: "owner_name", label: "Owner Name", column: 0 },
      { key: "owner_email", label: "Owner Email", column: 1 },
      { key: "owner_address1", label: "Owner Address 1", column: 0 },
      { key: "owner_address2", label: "Owner Address 2", column: 1 },
      { key: "owner_city", label: "Owner City", column: 0 },
      { key: "owner_state", label: "Owner State", column: 1 },
      { key: "owner_zip", label: "Owner ZIP Code", column: 0 },
      { key: "owner_phone", label: "Owner Phone", column: 1 },
    ],
  },
];

// Party (name/address/contact) field groups for mapping
export const PARTY_FIELD_GROUPS = [
  {
    title: "Contact Information",
    fields: [
      { key: "name", label: "Name", column: 0 },
      { key: "company_name", label: "Company name", column: 1 },
      { key: "email", label: "Email", column: 0 },
      { key: "phone", label: "Phone", column: 1 },
    ],
  },
  {
    title: "Address",
    fields: [
      { key: "address1", label: "Address 1", column: 0 },
      { key: "address2", label: "Address 2", column: 1 },
      { key: "city", label: "City", column: 0 },
      { key: "state", label: "State", column: 1 },
      { key: "zipcode", label: "Zipcode", column: 0 },
    ],
  },
  {
    title: "Question",
    fields: [
      {
        key: "suitable_question",
        label: "Suitable question",
        column: 0,
      },
    ],
  },
];

// Foreclosure case number upload onto existing licenses
export const CASE_NUMBER_FIELD_GROUPS = [
  {
    title: "Case Number Import",
    fields: [
      { key: "license_number", label: "License Number", column: 0 },
      { key: "case_number", label: "Case Number", column: 1 },
    ],
  },
];

export type ServiceTransferFixedValueType =
  | "party_or_registrant"
  | "boolean"
  | "date";

export type ServiceTransferFieldDef = {
  key: string;
  label: string;
  column: 0 | 1;
  fixedValueType?: ServiceTransferFixedValueType;
  /** When this field is a party/registrant pick, also fill these row keys. */
  pairedEmailKey?: string;
  pairedCompanyKey?: string;
  pairedAccountIdKey?: string;
};

// Operator bulk service transfer (JSON rows after client column map)
export const SERVICE_TRANSFER_FIELD_GROUPS: Array<{
  title: string;
  fields: ServiceTransferFieldDef[];
}> = [
  {
    title: "Match (any one)",
    fields: [
      { key: "license_number", label: "License Number", column: 0 },
      { key: "jurisdiction", label: "Jurisdiction", column: 1 },
      { key: "property_apn", label: "Property APN", column: 0 },
      { key: "case_number", label: "Case Number", column: 1 },
      { key: "property_address1", label: "Property Address", column: 0 },
      { key: "property_city", label: "Property City", column: 1 },
      { key: "property_state", label: "Property State", column: 0 },
      { key: "property_zipcode", label: "Property Zip", column: 1 },
    ],
  },
  {
    title: "Existing license filters (optional)",
    fields: [
      {
        key: "existing_registrant_name",
        label: "Existing Registrant",
        column: 0,
        fixedValueType: "party_or_registrant",
        pairedEmailKey: "existing_registrant_email",
        pairedCompanyKey: "existing_registrant_company_name",
      },
      {
        key: "existing_registrant_email",
        label: "Existing Registrant Email",
        column: 1,
      },
      {
        key: "existing_omt_company",
        label: "Existing OMT Company",
        column: 0,
        fixedValueType: "party_or_registrant",
      },
      {
        key: "existing_ams_company",
        label: "Existing AMS Company",
        column: 1,
        fixedValueType: "party_or_registrant",
      },
    ],
  },
  {
    title: "New registrant (required to commit)",
    fields: [
      {
        key: "new_registrant_name",
        label: "New Registrant",
        column: 0,
        fixedValueType: "party_or_registrant",
        pairedEmailKey: "new_registrant_email",
        pairedCompanyKey: "new_registrant_company_name",
        pairedAccountIdKey: "new_registrant_account_id",
      },
      {
        key: "new_registrant_email",
        label: "New Registrant Email",
        column: 1,
      },
    ],
  },
  {
    title: "Optional",
    fields: [
      {
        key: "new_omt_company",
        label: "New OMT Company",
        column: 0,
        fixedValueType: "party_or_registrant",
      },
      {
        key: "new_ams_company",
        label: "New AMS Company",
        column: 1,
        fixedValueType: "party_or_registrant",
      },
      {
        key: "service_transfer_date",
        label: "Service Transfer Date",
        column: 0,
        fixedValueType: "date",
      },
      {
        key: "pass_prior_unpaid_responsibility",
        label: "Pass Prior Unpaid",
        column: 1,
        fixedValueType: "boolean",
      },
      {
        key: "enact_omt_change",
        label: "Enact OMT Change",
        column: 0,
        fixedValueType: "boolean",
      },
    ],
  },
];

// Question field groups for mapping
export const QUESTION_FIELD_GROUPS = [
  {
    title: "Question Scope",
    fields: [
      { key: "scope", label: "Scope", column: 0 },
      { key: "license_type_name", label: "License Type Name", column: 1 },
      { key: "jurisdiction_name", label: "Jurisdiction Name", column: 0 },
      { key: "question_group_name", label: "Group", column: 1 },
    ],
  },
  {
    title: "Question Information",
    fields: [
      { key: "question", label: "Question Text", column: 0 },
      { key: "answer_type", label: "Answer Type", column: 1 },
      { key: "answer_required", label: "Answer Required", column: 0 },
      { key: "information_text", label: "Information Text", column: 1 },
    ],
  },
  {
    title: "Question Options",
    fields: [
      { key: "answer_options", label: "Answer Options", column: 0 },
      { key: "next_options", label: "Next Options", column: 1 },
      {
        key: "select_from_fixed_list",
        label: "Select From Fixed List",
        column: 0,
      },
      {
        key: "suggest_previous_property_managers",
        label: "Suggest Previously Entered Values",
        column: 1,
      },
      {
        key: "copy_from_question_ids",
        label: "Copy From Question Ids",
        column: 0,
      },
    ],
  },
  {
    title: "Display Settings",
    fields: [
      { key: "display_up_front", label: "Display Up Front", column: 0 },
      {
        key: "show_on_fees_payment_screen",
        label: "Show on Fees Payment Screen",
        column: 1,
      },
      { key: "display_order", label: "Display Order", column: 0 },
      { key: "next_question", label: "Next Question", column: 1 },
    ],
  },
  {
    title: "Live Link Settings",
    fields: [
      { key: "url_template", label: "URL Template", column: 0 },
      { key: "blank_answer_url", label: "Blank Answer URL", column: 1 },
      {
        key: "copy_value_to_clipboard",
        label: "Copy Value to Clipboard",
        column: 1,
      },
      { key: "http_method", label: "HTTP Method", column: 0 },
      {
        key: "value_extract_regex",
        label: "Value Extract Regex",
        column: 1,
      },
      { key: "request_fields", label: "Request Fields", column: 0 },
    ],
  },
  {
    title: "Photo Settings",
    fields: [
      {
        key: "allow_multiple_photos",
        label: "Allow Multiple Photos",
        column: 0,
      },
    ],
  },
  {
    title: "URL Settings",
    fields: [
      {
        key: "allow_multiple_urls",
        label: "Allow Multiple URLs",
        column: 0,
      },
    ],
  },
];

/** Bulk question group upload: group name + license type only. */
export const QUESTION_GROUP_FIELD_GROUPS = [
  {
    title: "Question Group",
    fields: [
      { key: "name", label: "Group Name", column: 0 },
      { key: "license_type_name", label: "License Type", column: 1 },
    ],
  },
];

// Function to get field label for display
export function getFieldLabel(
  fieldKey: string,
  questionLabels?: Record<string, string>,
): string {
  for (const group of QUESTION_GROUP_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  for (const group of QUESTION_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  // Check if it's a question field
  if (fieldKey.startsWith("question_")) {
    // If we have question labels, use them
    if (questionLabels?.[fieldKey]) {
      return questionLabels[fieldKey];
    }
    // Otherwise, use a generic label
    return "Question Field";
  }

  // Check license field groups
  for (const group of LICENSE_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  for (const group of LICENSE_PAYMENT_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  // Check unregistered field groups
  for (const group of UNREGISTERED_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  // Check party field groups
  for (const group of PARTY_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  for (const group of CASE_NUMBER_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  for (const group of SERVICE_TRANSFER_FIELD_GROUPS) {
    for (const field of group.fields) {
      if (field.key === fieldKey) {
        return field.label;
      }
    }
  }

  // Fallback to the field key itself
  return fieldKey;
}

// Function to check if a field is a question field
export function isQuestionField(fieldKey: string): boolean {
  return fieldKey.startsWith("question_");
}

/**
 * Strip unmapped license question keys before persisting a saved import mapping.
 * Matches backend `normalize_field_mappings` (empty `question_*` values are not stored).
 */
export function omitEmptyQuestionKeysFromFieldMappings(
  mappings: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(mappings)) {
    if (isQuestionField(k) && !(v ?? "").trim()) {
      continue;
    }
    out[k] = v;
  }
  return out;
}

export type QuestionMappingField = {
  key: string;
  label: string;
  column: number;
  /** Question-ID field keys represented by this one displayed mapping row. */
  aliasKeys: string[];
};

function normalizeQuestionLabel(label: string): string {
  return label.trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

// Function to get question fields from field mappings
export function getQuestionFields(
  fieldMappings: Record<string, string>,
  questionLabels?: Record<string, string>,
): QuestionMappingField[] {
  // Get all question fields from questionLabels (if available) to ensure all fields are shown
  const allQuestionFields = new Set<string>();

  // Add fields from fieldMappings (mapped fields)
  for (const [fieldKey] of Object.entries(fieldMappings)) {
    if (isQuestionField(fieldKey)) {
      allQuestionFields.add(fieldKey);
    }
  }

  // Add fields from questionLabels (all available question fields)
  if (questionLabels) {
    for (const fieldKey of Object.keys(questionLabels)) {
      if (isQuestionField(fieldKey)) {
        allQuestionFields.add(fieldKey);
      }
    }
  }

  // A question is stored once per jurisdiction/license type, so identical
  // question text can have several question_<uuid> keys. Show one mapping row
  // per displayed question and retain every key as an alias for fan-out.
  const grouped = new Map<
    string,
    { key: string; label: string; aliasKeys: string[] }
  >();
  for (const fieldKey of allQuestionFields) {
    const label = getFieldLabel(fieldKey, questionLabels);
    const normalizedLabel = normalizeQuestionLabel(label);
    const existing = grouped.get(normalizedLabel);
    if (existing) {
      existing.aliasKeys.push(fieldKey);
    } else {
      grouped.set(normalizedLabel, {
        key: fieldKey,
        label: label.trim().replace(/\s+/g, " "),
        aliasKeys: [fieldKey],
      });
    }
  }

  return Array.from(grouped.values()).map((field, columnIndex) => ({
    ...field,
    column: columnIndex % 2,
  }));
}

/**
 * Give every jurisdiction-specific key for the same question the same source
 * column. This also upgrades older saved mappings that only contain one alias.
 */
export function synchronizeQuestionMappingsByLabel(
  fieldMappings: Record<string, string>,
  questionLabels?: Record<string, string>,
): Record<string, string> {
  const synchronized = { ...fieldMappings };
  for (const field of getQuestionFields(fieldMappings, questionLabels)) {
    const representativeValue = synchronized[field.key]?.trim()
      ? synchronized[field.key]
      : field.aliasKeys
          .map((key) => synchronized[key])
          .find((value) => value?.trim());
    if (representativeValue === undefined) {
      continue;
    }
    for (const aliasKey of field.aliasKeys) {
      synchronized[aliasKey] = representativeValue;
    }
  }
  return synchronized;
}

export function getErrorFieldOrder(
  testResult: any,
  _questionLabels?: Record<string, string>,
  variant: "license" | "unregistered" = "license",
) {
  // Create a canonical order: standard fields first, then question fields
  const standardFields =
    variant === "unregistered"
      ? UNREGISTERED_FIELD_GROUPS.flatMap((group) =>
          group.fields.map((field) => field.key),
        )
      : [
          ...LICENSE_FIELD_GROUPS.flatMap((group) =>
            group.fields.map((field) => field.key),
          ),
          ...LICENSE_PAYMENT_FIELD_GROUPS.flatMap((group) =>
            group.fields.map((field) => field.key),
          ),
        ];

  // Get question fields from the sample data
  const sampleData = testResult?.sample_data?.[0];
  const questionFields = sampleData
    ? Object.keys(sampleData).filter((key) => key.startsWith("question_"))
    : [];

  // Return standard fields first, then question fields
  return [...standardFields, ...questionFields];
}
